import styled, { css } from 'styled-components'
import { Spacings, Text, Devices, COLORS } from '@styles'
import { LoadingSkeleton } from '@baseV2/composite/table'

export const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	width: 100%;
`

export const FieldsRow = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	align-items: end;
	gap: ${Spacings.SPACING_3B};
	width: 100%;

	@media ${Devices.tablet} {
		grid-template-columns: repeat(2, 1fr);
	}
`

export const FieldItem = styled.div`
	min-width: 0;

	& > div {
		width: 100%;
	}
`

export const TableCard = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-height: 28rem;
	overflow-y: auto;
	overflow-x: hidden;
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: 0;
`

export const TableHeaderRow = styled.div`
	display: flex;
	position: sticky;
	top: 0;
	z-index: 1;
	background: ${({ theme }) => theme.colors.surface.card};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const TableHeaderCell = styled.div`
	flex: ${({ flex }) => flex || '1 1 0%'};
	min-width: ${({ minWidth }) => minWidth || '0'};
	/* side padding matches the cells below, so headers stay over their own column */
	padding: ${Spacings.SPACING_10} ${Spacings.SPACING_2B};
	font-size: ${Text.TINY_SMALL};
	font-weight: ${Text.BOLD_500};
	line-height: 1rem;
	color: ${({ theme }) => theme.colors.text.primary};
	text-align: ${({ align }) => align || 'left'};
	overflow-wrap: anywhere;

	@media ${Devices.tablet} {
		overflow-wrap: normal;
	}
`

export const TableRow = styled.div`
	display: flex;
	align-items: flex-start;
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	&:last-of-type {
		border-bottom: none;
	}
`

export const TableCell = styled.div`
	flex: ${({ flex }) => flex || '1 1 0%'};
	min-width: ${({ minWidth }) => minWidth || '0'};
	/* a little more side padding than top/bottom, so neighbouring inputs do not read as one field */
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_2B};

	${({ align }) =>
		align === 'center' &&
		css`
			display: flex;
			align-items: center;
			justify-content: center;
		`}

	& > div {
		width: 100%;
	}
`

export const ConsentRow = styled.div`
	display: flex;
	align-items: center;
	width: 100%;

	@media ${Devices.tablet} {
		gap: ${Spacings.SPACING_2B};
	}
`

export const RequiredAsterisk = styled.span`
	color: ${COLORS.red[600]};
	margin-left: ${Spacings.SPACING_1};
`

export const ProductsCardHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	// flex-wrap: wrap;
	gap: ${Spacings.SPACING_3B};
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_10};
	background: ${({ theme }) => theme.colors.surface.overlay};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const ProductsTitleGroup = styled.div`
	display: flex;
	align-items: baseline;
	gap: ${Spacings.SPACING_1B};
	white-space: nowrap;
`

export const ProductsTitle = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
`

export const ProductsCount = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.brand.primary};
`

// Sized to closely match the real `Input`/`SearchInput` it stands in for (V2 `baseInputStyles`
// resolves to ~2.25rem tall at tablet+), so swapping skeleton <-> real content causes no layout shift.
export const SkeletonBlock = styled(LoadingSkeleton)`
	width: ${({ $width }) => $width || '100%'};
	height: ${({ $height }) => $height || '2.25rem'};
`

export const ProductsEmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: ${Spacings.SPACING_7B};
	width: 100%;
	min-height: 18rem;
	padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_4B};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: 0;
	text-align: center;
`

export const ProductsEmptyImage = styled.div`
	position: relative;
	flex-shrink: 0;
	width: 8.625rem;
	height: 6.25rem;
	overflow: hidden;

	& > img {
		position: absolute;
		top: -27.69%;
		left: -5.59%;
		width: 111.73%;
		height: 153.85%;
		max-width: none;
	}
`

export const ProductsEmptyTextGroup = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	width: 100%;
	max-width: 19.3125rem;
`

export const ProductsEmptyTitle = styled.span`
	font-size: ${Text.M_LARGE};
	font-weight: ${Text.BOLD_600};
	line-height: 1.625rem;
	color: ${({ theme }) => theme.colors.text.primary};
`

export const ProductsEmptyText = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_400};
	line-height: 1.375rem;
	color: ${COLORS.neutral[600]};
`

export const ActionMenuWrapper = styled.div`
	position: relative;
	display: inline-flex;
`

export const ActionMenuPanel = styled.div`
	position: fixed;
	/* Portaled to document.body (see RowActionMenu.jsx), so this must outrank the
	   global Drawer (z-index 1001, see global/Drawer/styles.js) it's opened from,
	   not just its old table-local siblings. */
	z-index: 9999;
	display: flex;
	flex-direction: column;
	min-width: 11rem;
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	box-shadow: ${({ theme }) => theme.shadows.md};
	padding: ${Spacings.SPACING_1B} 0;
`

export const ActionMenuItem = styled.button`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	width: 100%;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	background: none;
	border: none;
	font-family: inherit;
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	text-align: left;
	cursor: pointer;
	color: ${({ theme, $danger }) => ($danger ? theme.colors.status.error.text : theme.colors.text.primary)};

	svg {
		font-size: ${Text.MEDIUM};
		color: inherit;
	}

	&:hover {
		background: ${({ theme, $danger }) => ($danger ? theme.colors.status.error.bg : theme.colors.brand.subtle)};
		color: ${({ theme, $danger }) => ($danger ? theme.colors.status.error.text : theme.colors.brand.primary)};
	}
`

export const DescriptionPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	width: 100%;
	padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
	background: ${({ theme }) => theme.colors.surface.overlay};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const DescriptionPanelActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_3B};
`

export const DescriptionPreview = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_6B};
	background: ${({ theme }) => theme.colors.surface.overlay};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const DescriptionPreviewText = styled.span`
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: middle;
`
